// Main / branch picker for the structure tree (2026-11-01). One button shows what you are working
// on; it opens a panel with Main at the top and every open branch nested under the branch it was
// started from (`parent_id`). A branch whose parent is closed or merged hangs under its nearest
// open ancestor, else under Main.
import React, { useEffect, useMemo, useRef, useState } from 'react';

function buildTree(branches) {
  const open = new Map(branches.filter((b) => b.status === 'open').map((b) => [b.id, b]));
  const all = new Map(branches.map((b) => [b.id, b]));
  const parentOf = (b) => {
    let p = b.parent_id;
    const seen = new Set();
    while (p && !open.has(p) && !seen.has(p)) { seen.add(p); p = all.get(p)?.parent_id; }
    return p && open.has(p) ? p : 0;
  };
  const kids = new Map();
  for (const b of open.values()) {
    const p = parentOf(b);
    if (!kids.has(p)) kids.set(p, []);
    kids.get(p).push(b);
  }
  const rows = [];
  const walk = (pid, depth, trail) => {
    (kids.get(pid) || []).forEach((b, i, arr) => {
      const last = i === arr.length - 1;
      rows.push({ b, depth, trail, last, hasKids: kids.has(b.id) });
      walk(b.id, depth + 1, [...trail, !last]);
    });
  };
  walk(0, 1, []);
  return rows;
}

const BranchIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <circle cx="4" cy="3.5" r="1.7" /><circle cx="4" cy="12.5" r="1.7" /><circle cx="12" cy="5.5" r="1.7" />
    <path d="M4 5.2v5.6M12 7.2c0 2.6-2.2 3.3-6.4 4.4" />
  </svg>
);
const MainIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <circle cx="8" cy="3.5" r="1.7" /><circle cx="8" cy="12.5" r="1.7" /><path d="M8 5.2v5.6" />
  </svg>
);

export default function PsBranchPicker({ branches, branchId, onBranch, canEdit, onNew, onManage }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const box = useRef(null);
  const rows = useMemo(() => buildTree(branches), [branches]);
  const current = branches.find((b) => String(b.id) === String(branchId));
  const byId = useMemo(() => new Map(branches.map((b) => [b.id, b])), [branches]);

  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', esc); };
  }, [open]);

  const pick = (id) => { onBranch(id); setOpen(false); setQ(''); };
  const needle = q.trim().toLowerCase();
  // A search keeps the ancestors of a hit (faded) so the structure still reads.
  const shown = useMemo(() => {
    if (!needle) return rows.map((r) => ({ ...r, faded: false }));
    const hit = new Set(rows.filter((r) => r.b.name.toLowerCase().includes(needle)).map((r) => r.b.id));
    const keep = new Set(hit);
    for (const id of hit) {
      let p = byId.get(id)?.parent_id;
      while (p) { keep.add(p); p = byId.get(p)?.parent_id; }
    }
    return rows.filter((r) => keep.has(r.b.id)).map((r) => ({ ...r, faded: !hit.has(r.b.id) }));
  }, [rows, needle, byId]);

  // Lineage of the current branch: Main › parent › … › current
  const lineage = [];
  for (let b = current; b; b = b.parent_id ? byId.get(b.parent_id) : null) lineage.unshift(b);

  return (
    <div className="ps-bp" ref={box}>
      <button type="button" className={'ps-bp-btn' + (current ? ' ps-bp-btn--br' : '')} aria-haspopup="listbox" aria-expanded={open}
        onClick={() => setOpen((o) => !o)}>
        <span className="ps-bp-ico">{current ? <BranchIcon /> : <MainIcon />}</span>
        <span className="ps-bp-path">
          {current ? (
            <>
              <span className="ps-bp-crumb">Main</span>
              {lineage.slice(0, -1).map((b) => <span key={b.id} className="ps-bp-crumb">{b.name}</span>)}
              <b>{current.name}</b>
            </>
          ) : <b>Main</b>}
        </span>
        {current && <span className={'ps-bp-kind ps-bp-kind--' + current.kind}>{current.kind === 'variant' ? 'Variant' : 'Proposal'}</span>}
        <span className="ps-bp-count">{rows.length ? `${rows.length} branch${rows.length > 1 ? 'es' : ''}` : 'no branches'}</span>
        <span className="ps-bp-car" aria-hidden="true">▾</span>
      </button>

      {open && (
        <div className="ps-bp-pop" role="listbox">
          {rows.length > 5 && (
            <input className="ps-bp-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search branches…" autoFocus />
          )}
          <div className="ps-bp-list">
            <button type="button" role="option" aria-selected={!current}
              className={'ps-bp-row ps-bp-row--main' + (!current ? ' ps-bp-row--on' : '')} onClick={() => pick('')}>
              <span className="ps-bp-ico"><MainIcon /></span>
              <span className="ps-bp-name"><b>Main</b><small>The released structure</small></span>
              {!current && <span className="ps-bp-tick">✓</span>}
            </button>
            {shown.map(({ b, depth, trail, last, faded }) => {
              const on = current && current.id === b.id;
              return (
                <button key={b.id} type="button" role="option" aria-selected={on}
                  className={'ps-bp-row' + (on ? ' ps-bp-row--on' : '') + (faded ? ' ps-bp-row--faded' : '')}
                  onClick={() => pick(String(b.id))}>
                  <span className="ps-bp-guides" aria-hidden="true">
                    {trail.map((cont, i) => <span key={i} className={'ps-bp-g' + (cont ? ' ps-bp-g--line' : '')} />)}
                    <span className={'ps-bp-g ps-bp-g--elbow' + (last ? ' ps-bp-g--last' : '')} />
                  </span>
                  <span className="ps-bp-ico"><BranchIcon /></span>
                  <span className="ps-bp-name">
                    <b>{b.name}</b>
                    <small>
                      {b.changes.length} change{b.changes.length === 1 ? '' : 's'}
                      {b.created_by ? ` · ${b.created_by}` : ''}
                      {depth > 1 && byId.get(b.parent_id) ? ` · from ${byId.get(b.parent_id).name}` : ''}
                    </small>
                  </span>
                  <span className={'ps-bp-kind ps-bp-kind--' + b.kind}>{b.kind === 'variant' ? 'Variant' : 'Proposal'}</span>
                  {on && <span className="ps-bp-tick">✓</span>}
                </button>
              );
            })}
            {needle && !shown.length && <div className="ps-bp-empty">No branch matches “{q}”.</div>}
            {!rows.length && <div className="ps-bp-empty">No branches yet — start one to try a change without touching main.</div>}
          </div>
          <div className="ps-bp-foot">
            {canEdit && (
              <button type="button" className="ps-btn ps-btn--sm" onClick={() => { setOpen(false); onNew(current || null); }}>
                + New branch from {current ? current.name : 'Main'}
              </button>
            )}
            {onManage && <button type="button" className="ps-btn ps-btn--ghost ps-btn--sm" onClick={() => { setOpen(false); onManage(); }}>Manage branches</button>}
          </div>
        </div>
      )}
    </div>
  );
}
